// /groups/:groupId/events — the event box plus the archive.
//
// Two lists, because they answer different questions. The event box is
// "what is coming up" (upcoming, still open) and is the primary surface. The
// archive below it is everything else, so a closed game night is still reachable
// for its photos and results.

import { ApiClientError, fetchGroup, listEvents } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { GroupRole, type EventSummary, type GroupDetail } from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { eventCard } from './eventCard.js';

export const eventsView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let events: EventSummary[];
  let zone: string;
  try {
    [group, events, zone] = await Promise.all([
      fetchGroup(groupId),
      // One request for everything, split client-side. A group's event count is
      // small enough that two round trips would cost more than they save.
      listEvents(groupId, { limit: 200 }),
      viewerZone(),
    ]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.status === 403
          ? 'You are not a member of this group.'
          : 'Could not load events.',
      ),
      el('p', {}, [el('a', { href: '/groups' }, 'Back to your groups')]),
    );
    return;
  }

  // The event box cut is on the VIEWER's today. The server's `upcoming` filter
  // cuts on site-local today instead, which is the right cut for cron work but
  // would hide an event from someone a day behind the site zone.
  const today = todayInZone(zone);
  const upcoming = (e: EventSummary) => e.closedAt === null && e.eventDate >= today;
  const box = events.filter(upcoming);
  const archive = events.filter((e) => !upcoming(e)).reverse();

  const canManageEvents = group.myRole >= GroupRole.EVENTS_MANAGER;

  const header = el('div', { class: 'page-head' }, [
    el('h1', {}, `${group.name} events`),
    el('p', { class: 'muted' }, `Times shown in ${zone}`),
  ]);
  if (canManageEvents) {
    header.append(
      el('a', { class: 'btn', href: `/groups/${groupId}/events/new` }, 'New event'),
    );
  }

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}` }, `Back to ${group.name}`)]),
    header,
    groupSubnav(groupId, 'events', group.myRole >= GroupRole.ADMIN, groupRunsFasts(group.allowedEventTypes), groupShowsLeaderboard(group.allowedEventTypes)),
    eventsViewToggle(groupId, 'list', canManageEvents),
    el('h2', {}, 'Coming up'),
    box.length
      // Inline RSVP on the event box only: this is the surface people answer
      // from, and it must round-trip without navigating away.
      ? el('div', { class: 'event-list' }, box.map((e) => eventCard(e, zone, { rsvp: true })))
      : el('p', { class: 'muted' }, 'Nothing on the calendar yet.'),
    el('h2', {}, 'Past and closed'),
    archive.length
      ? el('div', { class: 'event-list' }, archive.map((e) => eventCard(e, zone)))
      : el('p', { class: 'muted' }, 'Nothing here yet.'),
  );
};

/**
 * The List/Calendar toggle, plus a Repeats link for whoever can manage events.
 *
 * Calendar and Repeating used to be their own top-level nav tabs. They still
 * have their own routes and views (calendar.ts, series.ts) — merging the code
 * would be a much bigger change for no real benefit — but the app now presents
 * them as one "Events" section with a view switch, not three separate places to
 * go looking.
 */
export function eventsViewToggle(
  groupId: number,
  active: 'list' | 'calendar',
  canManageEvents: boolean,
): HTMLElement {
  const listHref = `/groups/${groupId}/events`;
  const calendarHref = `/groups/${groupId}/calendar`;
  const links = [
    el('a', active === 'list' ? { href: listHref, class: 'active', 'aria-current': 'page' } : { href: listHref }, 'List'),
    el(
      'a',
      active === 'calendar' ? { href: calendarHref, class: 'active', 'aria-current': 'page' } : { href: calendarHref },
      'Calendar',
    ),
  ];
  if (canManageEvents) {
    links.push(el('a', { href: `/groups/${groupId}/series` }, 'Repeating events'));
  }
  return el('nav', { class: 'subnav' }, links);
}

/**
 * 'yyyy-MM-dd' for right now in `zone`.
 *
 * The 'en-CA' locale is the shortest way to get ISO-ordered parts out of
 * Intl without hand-assembling them, and it sidesteps every off-by-one that
 * comes from calling toISOString() on a local Date.
 */
export function todayInZone(zone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
